import { z } from 'zod';

import type { PracticeBuiltArm } from '../practice-builds/schema';

/**
 * The journey pathway's stored contract — the seam between the two sides of it.
 *
 * The composer writes a member's daily practices; the wayfinder home reads them
 * and renders tiles. Both need the same shapes, and neither owns the other, so
 * they live here rather than inside either feature. That is also why this module
 * carries only what BOTH sides touch: the tile copy, the stored content type, and
 * the two enum vocabularies the tables constrain themselves to. The event payloads
 * only the composer publishes and consumes stay with the composer.
 *
 * The contract lives in `@galapagos/shared` because `db/schema.ts` types its jsonb
 * columns from it (ADR-066 Rule 2) and cannot import a package that already depends
 * on this one — the same reason `home-plan/schema.ts` sits here.
 */

/**
 * Why a composition ran.
 *
 * A Zod enum as well as a SQL CHECK, deliberately. The CHECK is what makes a bad
 * value impossible to store; this is what lets the writer reject one before it
 * tries, and what stops each side re-deriving the same strings from prose.
 * The two are kept in step by a test asserting the CHECK admits exactly these.
 */
// `migration_reonboarding` is the first composition for a member migrated from
// Pelago 1.0: a fresh day like `nightly`, told apart so storage, analytics and
// the planning prompt can see it was a re-onboarding and not a routine night.
export const GENERATION_REASONS = [
  'nightly',
  'retrigger',
  'manual',
  'migration_reonboarding',
] as const;
export const generationReasonSchema = z.enum(GENERATION_REASONS);
export type GenerationReason = z.infer<typeof generationReasonSchema>;

/**
 * Where a practice's content came from.
 *
 * `authored` is a clinician-written prescribed technique, `generative` a
 * model-constructed one, `fallback` the degrade path when generation fails. The
 * distinction is not cosmetic: it is how an operator tells "the model is down" from
 * "the model is fine and members are getting authored content", and it rides on
 * every row so that question is answerable after the fact.
 */
export const SOURCE_TIERS = ['authored', 'generative', 'fallback'] as const;
export const sourceTierSchema = z.enum(SOURCE_TIERS);
export type SourceTier = z.infer<typeof sourceTierSchema>;

/**
 * Why the journey safety check left a slot empty.
 *
 * The four content classes are the question the check answers about a proposed
 * card (does it touch crisis, self-harm, active withdrawal, or medical
 * management); `check_failed` is the fail-closed arm — the check errored,
 * timed out, or answered outside its contract, and the slot was blocked
 * because no answer is treated as a yes. Closed by design: the decision row
 * and the operational event both carry one of these and never the model's
 * words, so no member text leaves the database through the veto.
 */
export const JOURNEY_VETO_CLASSES = [
  'crisis',
  'self_harm',
  'withdrawal',
  'medical',
  'check_failed',
] as const;
export const journeyVetoClassSchema = z.enum(JOURNEY_VETO_CLASSES);
export type JourneyVetoClass = z.infer<typeof journeyVetoClassSchema>;

/**
 * What put a member in scope for the journey safety check.
 *
 * `escalation` — any escalation on record, either source, open or handled,
 * any age. `context_keyword` — no escalation, but a safety term appeared in
 * the planner's rendered context. `escalation_unknown` — the escalation read
 * failed, so the member is treated as in scope rather than skipped: a read
 * failure must never be the reason a crisis card goes unchecked.
 */
export const JOURNEY_VETO_TRIGGERS = [
  'escalation',
  'context_keyword',
  'escalation_unknown',
  // The journey's default technique carries the planner's own intent, so
  // every member's default-technique card is checked: `intent_keyword` when a
  // safety term appears in the planner's intent or anchor itself, and
  // `default_technique` when the member was otherwise out of scope.
  'intent_keyword',
  'default_technique',
  // Set by the composer from the planner's own output: the class the planner
  // steered a slot away from (the slot still filled) or left the slot out
  // for (a miss). A guard that later blocks the card replaces it.
  'planner',
] as const;
export const journeyVetoTriggerSchema = z.enum(JOURNEY_VETO_TRIGGERS);
export type JourneyVetoTrigger = z.infer<typeof journeyVetoTriggerSchema>;

/**
 * Why the planner's choice for a slot did not stand. Recorded on the
 * composition decision row beside the planner's own intent, so a degraded
 * slot can be analysed against what the planner meant. Closed: a cause is a
 * code path, never model text.
 */
export const JOURNEY_DEGRADE_CAUSES = [
  'planning_failed',
  'technique_duplicate',
  'technique_out_of_set',
  'build_refused',
  'invalid_tile_copy',
  'gate_dropped',
] as const;
export const journeyDegradeCauseSchema = z.enum(JOURNEY_DEGRADE_CAUSES);
export type JourneyDegradeCause = z.infer<typeof journeyDegradeCauseSchema>;

/**
 * What happened to a degraded slot on its way to the default technique, when
 * something did: no planner intent to carry (`no_intent`), dropped as a
 * near-duplicate of another intent the member already has (`intent_duplicate`),
 * filled with the authored copy because the rewrite failed
 * (`copy_rewrite_failed`), or the default technique itself gated out for the
 * member (`gate_dropped`). Absent when the slot filled with rewritten copy.
 */
export const JOURNEY_FALLBACK_DETAILS = [
  'no_intent',
  'intent_duplicate',
  'copy_rewrite_failed',
  'gate_dropped',
] as const;
export const journeyFallbackDetailSchema = z.enum(JOURNEY_FALLBACK_DETAILS);
export type JourneyFallbackDetail = z.infer<typeof journeyFallbackDetailSchema>;

/**
 * The member-local day a composed practice or day-plan belongs to.
 *
 * Format only — which zone decided this string, and how a reader compares it
 * to "today", stay with the producer and reader respectively (ADR-095 Rules
 * 12/15; see the two tables' own comments in `db/schema.ts`). But the string
 * *shape* both sides already agreed on (the composer's spec pins this exact
 * regex) is a seam concern: the live-plan uniqueness index groups rows by
 * `day_key` as a plain string, so `2026-8-2` and `2026-08-02` would silently
 * coexist as two "live" plans for what a human reads as one day. Enforcing
 * the shape in Postgres, not just in the writer, is what the seam's own
 * partial-index philosophy (one live row per day, enforced by the database)
 * already asks for — it would be inconsistent to hold that bar for the
 * uniqueness index and not for the string the index groups by.
 */
export const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const dayKeySchema = z.string().regex(DAY_KEY_PATTERN);

/**
 * How many practice tiles the home renders from one day plan.
 *
 * A product constraint on the surface, not a page size: the strip is fixed and
 * a seventh tile has nowhere to go. It lives here rather than beside the
 * resolver that applies it because the presented-tiles event contract bounds
 * its arrangement by the same number — two literals with nothing tying them
 * together would let a cap change silently turn every home fetch into a
 * contract fault.
 */
export const JOURNEY_TILE_RENDER_CAP = 6;

/** The tile title's maximum rendered length. */
export const TILE_COPY_TITLE_MAX = 40;
/** The tile hook's maximum rendered length. */
export const TILE_COPY_HOOK_MAX = 90;

/**
 * The member-facing copy a practice tile renders.
 *
 * Written by the composer with the member's context in hand, and rendered verbatim
 * — the reader never truncates, reflows, or regenerates it. That is what makes the
 * caps a *contract* rather than a display hint: the only place a too-long string can
 * be stopped is before it is stored, because by the time the home renders it the
 * only remaining options are to overflow the tile or to drop the copy.
 *
 * Both bounds are enforced here and at the read boundary. Not redundant: the writer
 * checks what it is about to store, and the reader re-checks what it actually read,
 * because a row written before a cap changed is not hypothetical.
 *
 * `.strict()` so an extra field is a parse failure. This blob is stored and later
 * re-parsed, so a silently-carried unknown key would survive a round trip and read
 * as part of the contract to whoever found it.
 */
export const tileCopySchema = z
  .object({
    title: z.string().min(1).max(TILE_COPY_TITLE_MAX),
    hook: z.string().min(1).max(TILE_COPY_HOOK_MAX),
  })
  .strict();
export type TileCopy = z.infer<typeof tileCopySchema>;

/**
 * What became of a composed practice, member-side.
 *
 * `done` is written when the practice's launched conversation completes and
 * clears the member-turn floor; `skipped` is vocabulary only for now — the
 * composer's outcomes port speaks it, but the product decision that would
 * write it (expiry, deprioritisation) has not been taken, so no writer exists.
 * An outcome is terminal: the table's uniqueness makes the first write win and
 * nothing ever transitions or deletes one.
 */
export const PRACTICE_OUTCOMES = ['done', 'skipped'] as const;
export const practiceOutcomeSchema = z.enum(PRACTICE_OUTCOMES);
export type PracticeOutcome = z.infer<typeof practiceOutcomeSchema>;

/**
 * Which path decided an outcome.
 *
 * `client_report` is the fast path — the app reports observable call facts at
 * hang-up and the server decides in seconds; `transcript` is the reconciliation
 * path — the post-call webhook's projection, ~60–90s later, covering the cases
 * the client cannot report (crash, dead connection, lost report). Both run the
 * same decision and converge on one row; the source rides the row (the
 * `source` column) and the event, so each path's share is queryable and
 * chartable, and a duplicate arrival can distinguish "the other path already
 * decided" from a genuine cross-source disagreement.
 */
export const OUTCOME_SOURCES = ['client_report', 'transcript'] as const;
export const outcomeSourceSchema = z.enum(OUTCOME_SOURCES);
export type OutcomeSource = z.infer<typeof outcomeSourceSchema>;

/**
 * The member-turn floor a completed conversation must clear to mark its
 * practice done.
 *
 * Counts member-authored turns only — agent turns never satisfy it — so a
 * broken connection that died after the agent's greeting can never consume a
 * tile. The server is the only decider: the client reports its raw observed
 * count unconditionally and this constant (env-overridable per ADR-054) is
 * the default the decision service applies to BOTH paths — client-reported
 * and transcript-derived counts meet one threshold in one place.
 */
export const PRACTICE_DONE_MIN_MEMBER_TURNS = 5;

/**
 * How long the home may assert a replacement composition is pending after a
 * practice completion, measured from the newest done outcome's `occurred_at`.
 *
 * The serving path derives "a replacement is owed" purely from timestamps it
 * already reads — the newest done outcome postdating the live day-plan's
 * `created_at` — which is an inference, not process truth: a lost re-trigger
 * event, a trigger consumer not yet deployed, or a failed composer run all
 * leave that inference true forever. This bound is what keeps the home honest
 * in every one of those cases — past it the server stops asserting pending and
 * the next fetch clears the skeleton.
 *
 * Five minutes sits inside the product-decided 4–5 minute band, generous
 * against the ~90-second composer budget. Deliberately a constant rather than
 * env-dialled: the band was a product decision, and the server derivation is
 * its only consumer — the client shares the poll cadence, never this bound.
 */
export const REPLACEMENT_PENDING_TTL_MS = 5 * 60_000;

/**
 * The constructed practice's content, as stored.
 *
 * The column carries the shared `build_practice` built arm verbatim — the frozen
 * flat snake_case field set (`instructions` / `never` / `done_when` / `first_move`,
 * …) the voice model reads — so the stored blob and the wire arm are one shape with
 * one owner, `practiceBuiltArmSchema`. The type is the Zod-derived built-arm type
 * (ADR-066 Rule 2), imported type-only so this seam stays a pure vocabulary module.
 *
 * The declared type is a write-side promise, not a read-side guarantee: rows may
 * predate a schema change, so readers re-parse the payload with
 * `practiceBuiltArmSchema` at the read boundary and fail closed on a payload the
 * current contract no longer accepts. The serving path still never reads this
 * column — it renders `tile_copy` and nothing else.
 */
export type StoredPracticeContent = PracticeBuiltArm;
